/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { TextField, Stack, InputAdornment, Box } from "@mui/material";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import Checkbox from "@mui/material/Checkbox";
import "./ApplicationModal.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getConfig } from "../../config";

const filterItemsBySearch = (items, searchValue, name) => {
  return items.filter((item) => {
    if (name === "Countries" || name === "Vehicles") {
      return item.name?.toLowerCase().includes(searchValue.toLowerCase());
    }
    return item?.toLowerCase().includes(searchValue.toLowerCase());
  });
};

const filterSelectedBySearch = (items, searchValue, name) => {
  return items.filter((item) => {
    if (name === "Countries" || name === "Vehicles") {
      if (item.make) {
        return item.make.name?.toLowerCase().includes(searchValue.toLowerCase());
      }
      return item.name?.toLowerCase().includes(searchValue.toLowerCase());
    }
    return item?.toLowerCase().includes(searchValue.toLowerCase());
  });
};

const updateTabsLength = (tabs, selectedLength, availableLength) => {
  const newTabs = [...tabs];
  newTabs[1].length = selectedLength;
  newTabs[0].length = availableLength - selectedLength;
  return newTabs;
};

const processVehicleInitialization = (data, selected, selectedData, newSelected) => {
  const isAllVehiclesSelected = selected.length === 1 && 
    selected[0].make.all && selected[0].model.all && selected[0].year.all;

  if (isAllVehiclesSelected) {
    const obj = {
      make: { id: data.id, all: false, name: data.name },
      model: { ...selected[0].model },
      year: { ...selected[0].year },
    };
    newSelected.push(obj);
    data.targeted = true;
  } else {
    const index = selectedData.findIndex((dt) => dt.make.id === data.id);
    data.targeted = index !== -1;
  }
};

const processDefaultItem = (data, selectedData, selected, newSelected) => {
  if (data?.name && !data.models) {
    const index = selectedData.findIndex((dt) => dt.id === data.id);
    data.targeted = index !== -1;
  } else if (!data.id && !data.models) {
    const index = selectedData.indexOf(data);
    if (index !== -1) {
      newSelected.push(data);
    }
  } else {
    processVehicleInitialization(data, selected, selectedData, newSelected);
  }
};

function ApplicationModal({
  handleClose,
  selectedData,
  availabilty,
  name,
  sendPostSubmissionCall,
  status,
  readOnly = false,
}) {
  const [reasonForCountryExclusion, setReasonForCountryExclusion] =
    useState("");
  const [tabs, setTabs] = useState([
    {
      name: "Available",
      active: true,
      length: availabilty.length,
    },
    {
      name: "Targeted",
      active: false,
      length: selectedData.length,
    },
  ]);
  const [available, setAvailable] = useState(availabilty);
  const [disableButton, setDisableButton] = useState(false);
  const [selected, setSelected] = useState(selectedData);
  const [selectedAllVehicles, setSelectedAllVehicles] = useState([]);
  const [tabName, setTabName] = useState("Available");
  const [checkedIncludeAll, setCheckedIncludeAll] = useState(false);
  const [checkedExcludeAll, setCheckedExcludeAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAvailable, setFilteredAvailable] = useState(availabilty);
  const [filteredSelected, setFilteredSelected] = useState(selectedData);
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");

  const listRef = useRef(null);

  const handleSearchFilter = (event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);

    if (searchValue.trim() === "") {
      setFilteredAvailable(available);
      setFilteredSelected(selected);
      return;
    }

    setFilteredAvailable(filterItemsBySearch(available, searchValue, name));
    setFilteredSelected(filterSelectedBySearch(selected, searchValue, name));
  };

  const handleChangeReasonForCountryExclusion = (e) => {
    setReasonForCountryExclusion(e.target.value);
  };
 
  useEffect(() => {
    setFilteredAvailable(available);
  }, [available]);

  useEffect(() => {
    setFilteredSelected(selected);
  }, [selected]);

  const onCancel = () => {
    handleClose();
  };

  const changeTabName = (index) => {
    const newTab = [...tabs];
    newTab.forEach((ele) => {
      ele.active = false;
    });
    newTab[index].active = true;
    setTabName(index === 1 ? "Targeted" : "Available");
    setTabs(newTab);
  };

  const Addavailable = (data) => {
    if (disableButton) setDisableButton(false);
    if (checkedExcludeAll) setCheckedExcludeAll(false);
    
    const newSelected = [...selected];
    const hasIdNonVehicle = data?.id && name !== "Vehicles";
    const isNonVehicleData = data && !data.make && !data.id && name !== "Vehicles";
    const isVehicle = data?.id && name === "Vehicles";
    
    if (hasIdNonVehicle) {
      newSelected.push(data);
      setSelected([...newSelected]);
      const newAvaialble = [...available];
      const index = newAvaialble.findIndex((e) => e.id === data.id);
      newAvaialble[index].targeted = true;
      setAvailable([...newAvaialble]);
      setTabs(updateTabsLength(tabs, newSelected.length, available.length));
      setCheckedIncludeAll(false);
    } else if (isNonVehicleData && data === "ALL") {
      const newCPUSelected = [...available];
      setSelected([...newCPUSelected]);
      setCheckedIncludeAll(true);
      setTabs(updateTabsLength(tabs, newCPUSelected.length, available.length));
      setCheckedIncludeAll(false);
    } else if (isNonVehicleData) {
      newSelected.push(data);
      setSelected([...newSelected]);
      if (checkedIncludeAll) setCheckedIncludeAll(false);
      setTabs(updateTabsLength(tabs, newSelected.length, available.length));
      setCheckedIncludeAll(false);
    } else if (isVehicle) {
      setSelectedAllVehicles([]);
      const obj = {
        make: { id: data.id, name: data.name, all: false },
        model: { id: null, all: true },
        year: { year: null, all: true },
      };
      newSelected.push(obj);
      data.targeted = true;
      setSelected([...newSelected]);
      setTabs(updateTabsLength(tabs, newSelected.length, available.length));
      setCheckedIncludeAll(false);
    }
  };

  const RemoveFormSelected = (data) => {
    const newSeledtedData = [...selected];
    const newAvailable = [...available];
    let finalSelectedLength;
    
    const hasId = data?.id;
    const isAllData = data === "ALL";
    const hasNoIdNoMake = data && !data.id && !data.make;
    
    if (hasId) {
      const index = newSeledtedData.findIndex((e) => e.id === data.id);
      newSeledtedData.splice(index, 1);
      const availableIndex = newAvailable.findIndex((e) => e.id === data.id);
      newAvailable[availableIndex].targeted = false;
      setAvailable([...newAvailable]);
      setSelected([...newSeledtedData]);
      finalSelectedLength = newSeledtedData.length;
    } else if (hasNoIdNoMake && isAllData) {
      setSelected([]);
      setDisableButton(true);
      finalSelectedLength = 0;
    } else if (hasNoIdNoMake) {
      const index = newSeledtedData.indexOf(data);
      newSeledtedData.splice(index, 1);
      const AllIndex = newSeledtedData.indexOf("ALL");
      if (AllIndex !== -1) {
        newSeledtedData.splice(AllIndex, 1);
      }
      setSelected([...newSeledtedData]);
      setDisableButton(false);
      finalSelectedLength = newSeledtedData.length;
    } else {
      setSelectedAllVehicles([]);
      const index = newSeledtedData.findIndex((e) => e.make.id === data.make.id);
      newSeledtedData.splice(index, 1);
      const availableIndex = newAvailable.findIndex((dt) => dt.id === data.make.id);
      newAvailable[availableIndex].targeted = false;
      setAvailable([...newAvailable]);
      setSelected([...newSeledtedData]);
      finalSelectedLength = newSeledtedData.length;
    }
    setTabs(updateTabsLength(tabs, finalSelectedLength, available.length));
    setCheckedIncludeAll(false);
    console.log("final selected length",finalSelectedLength)
  };
  useEffect(() => {
    const newAvaiable = [...availabilty];
    const newSelected = [];
    
    if (name === "CPU" && selected.length === 1 && selected[0] === "ALL") {
      available.forEach((ele) => {
        newSelected.push(ele);
      });
      setSelected([...newSelected]);
      setCheckedIncludeAll(true);
    } else if (newAvaiable.length === selected.length) {
      setCheckedIncludeAll(true);
      if (name === "Countries") {
        newAvaiable.forEach((data) => {
          const index = selectedData.findIndex((dt) => dt.id === data.id);
          data.targeted = index !== -1;
        });
      } else if (name === "Vehicles") {
        newAvaiable.forEach((data) => {
          processVehicleInitialization(data, selected, selectedData, newSelected);
        });
      }
    } else {
      newAvaiable.forEach((data) => {
        processDefaultItem(data, selectedData, selected, newSelected);
      });
    }
    
    setAvailable([...newAvaiable]);
    
    if (name !== "Countries" && newSelected && newSelected.length > 0) {
      setSelected([...newSelected]);
      const shouldCheck = newAvaiable.length !== newSelected.length && 
        (name === "cpu" || name === "Countries");
      setCheckedIncludeAll(shouldCheck);
      setTabs(updateTabsLength(tabs, newSelected.length, available.length));
    } else {
      setTabs(updateTabsLength(tabs, selected.length, available.length));
    }
  }, []);

  const sendSubmission = () => {
    switch (name) {
      case "Countries":
        sendPostSubmissionCall(selected, reasonForCountryExclusion);
        handleClose();
        break;
      case "CPU":
        sendPostSubmissionCall(selected);
        handleClose();
        break;
      case "Vehicles":
        if (selectedAllVehicles && selectedAllVehicles.length > 0) {
          sendPostSubmissionCall(selectedAllVehicles);
        } else {
          sendPostSubmissionCall(selected);
        }
        handleClose();
        break;
      default:
        handleClose();
        break;
    }
  };

  const handleIncludeAll = (event) => {
    setCheckedIncludeAll(event.target.checked);
    if (checkedExcludeAll) {
      setCheckedExcludeAll(false);
    }
    if (event.target.checked) {
      switch (name) {
        case "Countries": {
          const newAvaiable1 = [...available];
          setSelected([...newAvaiable1]);
          newAvaiable1.forEach((data) => {
            data.targeted = true;
          });
          break;
        }
        case "Vehicles": {
          const newAvailable1 = [...available];
          const newSelected1 = [];
          newAvailable1.forEach((data) => {
            data.targeted = true;
            const obj = {};
            obj.make = {
              id: data.id,
              name: data.name,
              all: false,
            };
            obj.model = {
              id: null,
              all: true,
            };
            obj.year = {
              year: null,
              all: true,
            };
            newSelected1.push(obj);
          });
          const allVehiclesSelect = [];
          const allObj = {};
          allObj.make = {
            id: null,
            all: true,
          };
          allObj.model = {
            id: null,
            all: true,
          };
          allObj.year = {
            id: null,
            all: true,
          };

          allVehiclesSelect.push(allObj);
          setAvailable([...newAvailable1]);
          setSelected([...newSelected1]);
          setSelectedAllVehicles([...allVehiclesSelect]);
          break;
        }
        case "CPU": {
          const newCPUSelected1 = [];
          available.forEach((ele) => {
            newCPUSelected1.push(ele);
          });
          setSelected([...newCPUSelected1]);
          break;
        }
      }
      const newTabs = [...tabs];
      newTabs[1].length = available.length;
      newTabs[0].length = 0;
      setTabs(newTabs);
      setDisableButton(false);
    } else {
      clearAllSelections();
    }
  };

  const clearAllSelections = () => {
    setDisableButton(true);
    setSelected([]);
    if (name !== "CPU") {
      const newAvailable = [...available];
      newAvailable.forEach((data) => {
        data.targeted = false;
      });
      if (name === "Vehicles") {
        setAvailable([...newAvailable]);
        setSelectedAllVehicles([]);
      }
    }
    const newTabs = [...tabs];
    newTabs[1].length = 0;
    newTabs[0].length = available.length;
    setTabs(newTabs);
  };

  const handleExcludeAll = (event) => {
    setCheckedExcludeAll(event.target.checked);
    setCheckedIncludeAll(false);
    if (event.target.checked) {
      clearAllSelections();
    }
  };

  const addPlaceHolder = () => {
    const placeholders = {
      Countries: "Search for countries",
      Vehicles: "Search for make",
      CPU: "Search for cpu",
    };
    return placeholders[name] ?? "";
  };

  const canEdit =
    (getConfig().REACT_APP_PORTAL === "seller" && status === "DRAFT") ||
    (getConfig().REACT_APP_PORTAL === "domain-approver" &&
      (status === "SUBMITTED_FOR_STAGING" || status === "IN_REVIEW") &&
      !thirdPartyApps) ||
    (getConfig().REACT_APP_PORTAL === "global-admin" && status === "IN_REVIEW");

  const renderAvailableItemIcon = (data) => {
    const hasId = data?.id;
    const isTargeted = data?.targeted;
    const isInSelected = selected.includes(data);
    const isDraftSeller = getConfig().REACT_APP_PORTAL === "seller" && status === "DRAFT";

    if (hasId && isTargeted && canEdit) {
      return (
        <RemoveCircleOutlineIcon
          style={{ width: "16px", height: "16px", color: "red" }}
          onClick={() => RemoveFormSelected(data)}
          disabled={name === "Vehicles" && data.name === "Harman"}
        />
      );
    }

    if (hasId && !isTargeted && canEdit) {
      return (
        <AddCircleOutlineIcon
          style={{ width: "16px", height: "16px" }}
          onClick={() => Addavailable(data)}
        />
      );
    }

    if (isInSelected && canEdit) {
      return (
        <RemoveCircleOutlineIcon
          style={{ width: "16px", height: "16px", color: "red" }}
          onClick={() => RemoveFormSelected(data)}
        />
      );
    }

    if (isDraftSeller) {
      return (
        <AddCircleOutlineIcon
          style={{ width: "16px", height: "16px" }}
          onClick={() => Addavailable(data)}
        />
      );
    }

    return null;
  };

  return (
    <div>
      <div className="modal-header">
        <TextField
          type="search"
          placeholder={addPlaceHolder()}
          size="small"
          value={searchTerm}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          onChange={handleSearchFilter}
          fullWidth
        />
      </div>
      <div className="modal-main-content">
        <Stack className="stack-line" direction="row" spacing={2}>
          {tabs.map((tab, index) => {
            return (
              <button
                type="button"
                onClick={() => changeTabName(index)}
                key={tab.name}
                className={
                  tab.active
                    ? "tabname-index active"
                    : "tabname-index not-active"
                }
              >
                <div>{tab.name}</div>
                {canEdit && (
                  <div className={tab.active ? "round" : "not-round"}>
                    {tab.length}
                  </div>
                )}
              </button>
            );
          })}
        </Stack>
      </div>
      <div className="modal-scrollable-content">
        <Stack ref={listRef} direction="column" spacing={0.5}>
          {tabName === "Available"
            ? filteredAvailable &&
              filteredAvailable.length > 0 &&
              filteredAvailable.map((data, index) => {
                return (
                  <div
                    key={data?.id ?? index}
                    className={index % 2 === 0 ? "contents" : "contents bgColor"}
                  >
                    {data?.id ? (
                      <div
                        id={data.name}
                        className={
                          data.targeted
                            ? "data-name data-name-bold"
                            : "data-name"
                        }
                      >
                        {data?.name ?? data}
                      </div>
                    ) : (
                      <div
                        id={data}
                        className={
                          Array.isArray(selected) &&
                          selected?.includes(data)
                            ? "data-name data-name-bold"
                            : "data-name"
                        }
                      >
                        {data?.name ?? data}
                      </div>
                    )}

                    <Button>
                      {renderAvailableItemIcon(data)}
                    </Button>
                  </div>
                );
              })
            : Array.isArray(filteredSelected) &&
              filteredSelected &&
              filteredSelected.length > 0 &&
              filteredSelected.map((data, index) => {
                return data.make ? (
                  <div
                    key={data?.make?.id ?? index}
                    className={index % 2 === 0 ? "contents" : "contents bgColor"}
                  >
                    <div className="data-name">
                      {data?.make?.name ?? data}
                    </div>
                    <Button>
                      {status === "DRAFT" && (
                        <RemoveCircleOutlineIcon
                          style={{
                            width: "16px",
                            height: "16px",
                            color: "red",
                          }}
                          onClick={() => RemoveFormSelected(data)}
                          disabled={data.make.name === "Harman"}
                        />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div
                    key={data?.id ?? index}
                    className={index % 2 === 0 ? "contents" : "contents bgColor"}
                  >
                    <div className="data-name">
                      {data?.name ?? data}
                    </div>
                    <Button>
                      {canEdit && (
                        <RemoveCircleOutlineIcon
                          style={{
                            width: "16px",
                            height: "16px",
                            color: "red",
                          }}
                          onClick={() => RemoveFormSelected(data)}
                        />
                      )}
                    </Button>
                  </div>
                );
              })}
        </Stack>
      </div>

      {name === "Countries" &&
        getConfig().REACT_APP_PORTAL === "global-admin" && (
          <Box
            className="reason-textarea"
            sx={{
              paddingTop: "16px",
            }}
          >
            <textarea
              placeholder="Reason For Country Exclusion"
              value={reasonForCountryExclusion}
              onChange={handleChangeReasonForCountryExclusion}
              disabled={readOnly}
              style={{
                width: "89ch",
                minHeight: "45px",
                maxHeight: "60px",
                padding: "13px 0px 0px 13px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </Box>
        )}

      <div className="footer">
        {canEdit && (
          <div>
            {tabs[0].active && (
              <div>
                <Checkbox
                  label="Inclue all"
                  checked={checkedIncludeAll}
                  onChange={handleIncludeAll}
                  disabled={tabs[0].length === 0}
                  size="small"
                />
                <span className="include-all">Include All</span>
              </div>
            )}
            {tabs[1].active && (
              <div>
                <Checkbox
                  label="Exclude All"
                  checked={checkedExcludeAll}
                  onChange={handleExcludeAll}
                  disabled={selected.length === 0}
                  size="small"
                />
                <span className="include-all">Exclude All</span>
              </div>
            )}
          </div>
        )}
        {canEdit && (
          <div>
            <Button className="cancel" onClick={onCancel}>
              Cancel
            </Button>

            <Button
              variant="outlined"
              sx={{
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26) !important",
                  border: "1px solid rgba(0, 0, 0, 0.12) !important",
                },
              }}
              className="save"
              onClick={sendSubmission}
              disabled={disableButton || tabs[1].length === 0}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

ApplicationModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  selectedData: PropTypes.array.isRequired,
  availabilty: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  sendPostSubmissionCall: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};

export default ApplicationModal;

